import configs from "./configs";
import Cookies from "js-cookie";

const OPENAI_API = configs["OPENAI_API"];
export const OPENAI_AGENT_URL = `${OPENAI_API}/v1/ai`; // Export OPENAI_AGENT_URL
export const OPENAI_AUDIO_URL = `${OPENAI_API}/v1/audios`;
export const PROVIDERS = {
  OPEN_AI_ASSISTANT: "OpenAI Assistant",
  OPEN_AI_COMPLETION: "OpenAI Completion",
  AZURE_AI: "Azure AI"
};

import { fetchEventSource } from "@microsoft/fetch-event-source";

export function getAiEntities() {
  return document.querySelectorAll("[npc-avatar]");
}

export function getCookieToken() {
  return localStorage.getItem("auth0Token") || Cookies.get(configs["JWT_COOKIE"]);
}

export function getAIEntity(aiThread) {
  let entity;
  document.querySelectorAll("[npc-avatar]").forEach(el => {
    if (el.components["npc-avatar"].npcId == aiThread) {
      entity = el;
    }
  });
  return entity;
}

export function getNpcComponent(aiEntity) {
  return aiEntity?.components["npc-avatar"] || aiEntity?.components["ai-agent"];
}

function randomString(len) {
  const p = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return [...Array(len)].reduce(a => a + p[~~(Math.random() * p.length)], "");
}

export async function createThread(apiKey) {
  /*
The response from the OpenAI API is a JSON object with the following structure:

- id: A unique identifier for the thread.
- object: The type of the object, in this case "thread".
- created_at: The timestamp of when the thread was created.
- metadata: An array of metadata associated with the thread.
- tool_resources: An array of tool resources associated with the thread.
*/
  const cookie = getCookieToken();
  try {
    const response = await fetch(`${OPENAI_AGENT_URL}/thread`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${cookie}`,
        Accept: "application/json",
        "X-AI-API-KEY": apiKey
      }
    });

    const promptResponse = await response.json();
    return promptResponse?.id;
  } catch (e) {
    console.error(e);
    return false;
  }
}

export async function getPromptIntention(prompt /* Removed unused apiKey */) {
  /*
The response from the OpenAI API is a JSON object with the following structure:

- text: string with the response to the prompt.
*/
  const cookie = getCookieToken();
  let promptResponse = "text";
  try {
    const response = await fetch(`${OPENAI_AGENT_URL}/prompt/intention`, {
      method: "POST",
      body: JSON.stringify({ prompt }),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${cookie}`,
        Accept: "application/json"
      }
    });

    promptResponse = await response.json();
    promptResponse.response = promptResponse.response === "image" ? promptResponse.response : "text";
  } catch (e) {
    console.error("Error sending thread prompt", e);
  }

  return promptResponse.response?.toLowerCase();
}

export function sendThreadPrompt({ thread, prompt, onMessageChunk, onEnd, onError, controller, onRetry, uuid }) {
  const cookie = getCookieToken();
  let url;
  const headers = {
    Authorization: `Bearer ${cookie}`
  };

  if (uuid) {
    url = `${OPENAI_AGENT_URL}/thread/text/completion?uuid=${uuid}&prompt=${encodeURIComponent(prompt)}`;
    if (thread) {
      url += `&threadId=${thread}`;
    }
  } else {
    return;
  }

  const maxRetries = 10;
  const retryDelay = 3000;
  let attempt = 0;
  let successReceived = false;

  const attemptSend = () => {
    if (controller.signal.aborted) {
      return;
    }

    fetchEventSource(url, {
      headers,
      openWhenHidden: true,
      onmessage(event) {
        if (!event.data || event.event === "error") return;
        const data = JSON.parse(event.data);

        if (data.type === "error") {
          let errorMessage = data.error || "";

          console.error("Error from OpenAI API:", errorMessage);

          if (errorMessage) {
            if (errorMessage?.toLowerCase().includes("incorrect api key provided")) {
              errorMessage = "Invalid Credentials";
            }
          }

          // Check for the specific error message
          if (errorMessage.includes("Can't add messages to thread" || "has an active run")) {
            if (attempt < maxRetries && !successReceived) {
              onRetry();
              attempt++;
              console.warn(
                `Attempt ${attempt} failed with error: "${errorMessage}". Retrying in ${retryDelay / 1000} seconds...`
              );
              setTimeout(() => attemptSend(), retryDelay);
            } else {
              console.error(`Max retries (${maxRetries}) reached. Handling error.`);
              if (onError) onError(errorMessage);
              controller.abort();
            }
          } else {
            // Handle other types of errors
            if (onError) onError(errorMessage);
            controller.abort();
            throw new Error(errorMessage);
          }
          return;
        }

        // Handle normal message chunks
        successReceived = true;
        if (onMessageChunk) {
          onMessageChunk(data);
        }
      },
      onerror(err) {
        // Handle network or parsing errors
        console.error("Fetch error:", err);
        if (onError) onError(err);
        throw err;
      },
      onclose() {
        // Connection closed by the server
        if (onEnd) onEnd();
      }
    });
  };

  // Start the first attempt
  attemptSend();

  // Return the controller to allow external abortion if needed
  return controller;
}

export async function sendAudioPrompt(audio /* Removed unused apiKey */) {
  const formData = new FormData();
  formData.append("file", audio, `recording-${randomString(6)}.mp3`);
  /*
The response from the OpenAI API is a JSON object with the following structure:

- id: A unique identifier for the completion.
- object: The type of the object, in this case "chat.completion".
- created: The timestamp of when the completion was created.
- model: The model used to generate the completion.
- choices: An array of choices. Each choice has:
  - index: The index of the choice.
  - message: An object with the role ("assistant" in this case) and the content of the message.
  - logprobs: The log probabilities of the tokens in the message, if requested.
  - finish_reason: The reason the model stopped generating further tokens.
- usage: An object with the number of tokens used in the prompt, the completion, and total.
- system_fingerprint: A system fingerprint, if any.
*/
  const cookie = getCookieToken();
  let message;
  try {
    const response = await fetch(`${OPENAI_AGENT_URL}/audio/complete`, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${cookie}`
      }
    });

    const promptResponse = await response.json();
    message =
      promptResponse?.choices && promptResponse?.choices.length
        ? promptResponse?.choices[0]?.message?.content
        : "Sorry, I could not understand that.";
  } catch (e) {
    console.error(e);
    message = "Sorry, there was an error processing the audio";
  }

  return message;
}

// audio: Blob
export async function sendAudioTranscription({ audio, signal, uuid /* Removed apiKey */ }) {
  const formData = new FormData();
  formData.append("file", audio, `recording-${randomString(6)}.mp3`);
  formData.append("uuid", uuid);

  /*
The response from the OpenAI API is a JSON object with the following structure:
- text: string with audio transcribed.
*/
  const cookie = getCookieToken();
  const url = `${OPENAI_AGENT_URL}/audio/transcribe?uuid=${uuid}`;
  const response = await fetch(url, {
    method: "POST",
    body: formData,
    headers: {
      Authorization: `Bearer ${cookie}`
    },
    signal
  });

  const promptResponse = await response.json();
  return promptResponse?.text;
}

export async function getAudioSpeech(audio) {
  const response = await fetch(`${OPENAI_AUDIO_URL}/${audio}`, {
    method: "GET",
    body: JSON.stringify()
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const blob = new Blob([arrayBuffer], { type: "audio/wav" }); // Adjust the MIME type as needed
  const url = URL.createObjectURL(blob);

  return url;
}

export async function getAudioTTSOpenAI({ uuid, prompt, signal /* Removed apiKey */ }) {
  const cookie = getCookieToken();
  const response = await fetch(`${OPENAI_AGENT_URL}/completion/audio`, {
    method: "POST",
    body: JSON.stringify({ prompt, uuid }),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${cookie}`,
      Accept: "application/json"
    },
    signal
  });

  if (!response.ok) {
    throw new Error(`TTS request failed with status ${response.status}`);
  }

  return response;
}
